import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ChevronLeft, ChevronRight, X, Minimize2 } from 'lucide-react';

// Wrap each inline <script> in an IIFE so multiple Chart.js blocks don't
// clash on `const ctx` (or any other top-level const/let) in the shared
// global scope of the iframe document.
const wrapScriptsInIIFE = (html) => {
  if (!html) return html;
  return html.replace(
    /<script(?![^>]*\bsrc\b)([^>]*)>([\s\S]*?)<\/script>/gi,
    (match, attrs, body) => {
      if (!body.trim()) return match;
      return `<script${attrs}>(function(){${body}})();</script>`;
    }
  );
};

export default function PresentationViewer({ slides, startIndex = 0, onClose, title }) {
  const [currentIndex, setCurrentIndex] = useState(startIndex);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [iframeScale, setIframeScale] = useState(1);
  const containerRef = useRef(null);
  const iframeRef = useRef(null);
  const hideTimer = useRef(null);

  const totalSlides = slides.length;

  const goNext = useCallback(() => {
    setCurrentIndex(prev => Math.min(prev + 1, totalSlides - 1));
  }, [totalSlides]);

  const goPrev = useCallback(() => {
    setCurrentIndex(prev => Math.max(prev - 1, 0));
  }, []);

  // Auto-enter fullscreen on mount
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.requestFullscreen().catch(() => {});
    }
  }, []);

  // Compute iframe scale to fit 1280x720 into the viewport
  useEffect(() => {
    const updateScale = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      setIframeScale(Math.min(w / 1280, h / 720));
    };
    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'PageDown') {
        e.preventDefault();
        goNext();
      } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        e.preventDefault();
        goPrev();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        if (document.fullscreenElement) {
          document.exitFullscreen();
        }
        onClose();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [goNext, goPrev, onClose]);

  // Auto-hide controls after inactivity
  useEffect(() => {
    const resetTimer = () => {
      setShowControls(true);
      if (hideTimer.current) clearTimeout(hideTimer.current);
      hideTimer.current = setTimeout(() => setShowControls(false), 3000);
    };
    window.addEventListener('mousemove', resetTimer);
    resetTimer();
    return () => {
      window.removeEventListener('mousemove', resetTimer);
      if (hideTimer.current) clearTimeout(hideTimer.current);
    };
  }, []);

  // Track fullscreen state — close viewer when exiting fullscreen
  useEffect(() => {
    const handleFSChange = () => {
      const fs = !!document.fullscreenElement;
      setIsFullscreen(fs);
      if (!fs) onClose();
    };
    document.addEventListener('fullscreenchange', handleFSChange);
    return () => document.removeEventListener('fullscreenchange', handleFSChange);
  }, [onClose]);

  const currentSlide = slides[currentIndex];

  // Ref callback: when a new iframe mounts (key changes), set up keyboard
  // forwarding and Chart.js reinit after load.
  const handleIframeRef = useCallback((iframe) => {
    iframeRef.current = iframe;
    if (!iframe) return;

    const onLoad = () => {
      try {
        const iDoc = iframe.contentDocument || iframe.contentWindow?.document;
        if (!iDoc) return;

        // Forward keyboard events so arrow-key navigation still works
        iDoc.addEventListener('keydown', (e) => {
          window.dispatchEvent(new KeyboardEvent('keydown', {
            key: e.key, code: e.code, bubbles: true,
          }));
        });

        // Reinit Chart.js if CDN loaded after inline scripts ran
        const iWin = iframe.contentWindow;
        const canvases = iDoc.querySelectorAll('canvas');
        if (canvases.length > 0 && iWin?.Chart?.getChart) {
          const allRendered = Array.from(canvases).every(c => iWin.Chart.getChart(c));
          if (!allRendered) {
            // Destroy partial instances, re-execute inline scripts (already IIFE-wrapped)
            canvases.forEach(c => { try { iWin.Chart.getChart(c)?.destroy(); } catch (_) {} });
            iDoc.querySelectorAll('script:not([src])').forEach(old => {
              const fresh = iDoc.createElement('script');
              fresh.textContent = old.textContent;
              old.parentNode.replaceChild(fresh, old);
            });
          }
        }
      } catch (_) { /* cross-origin safety */ }
    };

    iframe.addEventListener('load', onLoad, { once: true });
  }, []);

  // Keep focus on the container so keyboard events work
  useEffect(() => {
    if (containerRef.current) containerRef.current.focus();
  }, [currentIndex]);

  if (!currentSlide) return null;

  const isHtmlSlide = !!currentSlide.html_source;
  const slideUrl = currentSlide.preview_url;

  return (
    <div
      ref={containerRef}
      tabIndex={-1}
      className="fixed inset-0 z-[9999] bg-black flex flex-col select-none outline-none"
      onClick={(e) => {
        // Click on left/right halves to navigate
        const rect = e.currentTarget.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        if (clickX < rect.width * 0.3) goPrev();
        else if (clickX > rect.width * 0.7) goNext();
      }}
    >
      {/* Slide display */}
      <div className="flex-1 flex items-center justify-center overflow-hidden">
        {isHtmlSlide ? (
          <div className="w-full h-full flex items-center justify-center">
            <iframe
              key={`present-slide-${currentIndex}`}
              ref={handleIframeRef}
              srcDoc={wrapScriptsInIIFE(currentSlide.html_source)}
              sandbox="allow-scripts allow-same-origin"
              tabIndex={-1}
              style={{
                width: '1280px',
                height: '720px',
                border: 'none',
                transform: `scale(${iframeScale})`,
                transformOrigin: 'center center',
                pointerEvents: 'none',
              }}
              title={`Slide ${currentIndex + 1}`}
            />
          </div>
        ) : currentSlide.video_url ? (
          <video
            src={currentSlide.video_url}
            className="max-w-full max-h-full object-contain"
            autoPlay
            loop
            muted
          />
        ) : slideUrl ? (
          <img
            src={slideUrl}
            alt={`Slide ${currentIndex + 1}`}
            className="max-w-full max-h-full object-contain"
            draggable={false}
          />
        ) : (
          <div className="text-white/50 text-lg">No preview available</div>
        )}
      </div>

      {/* Controls overlay */}
      <div
        className={`absolute inset-0 pointer-events-none transition-opacity duration-300 ${
          showControls ? 'opacity-100' : 'opacity-0'
        }`}
      >
        {/* Top bar */}
        <div className="absolute top-0 left-0 right-0 pointer-events-auto bg-gradient-to-b from-black/60 to-transparent px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-white/80 text-sm font-medium truncate max-w-[400px]">
              {title || 'Presentation'}
            </span>
            <span className="text-white/50 text-xs">
              {currentIndex + 1} / {totalSlides}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {isFullscreen && (
              <button
                onClick={(e) => { e.stopPropagation(); document.exitFullscreen(); }}
                className="p-2 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                title="Exit fullscreen (Esc)"
              >
                <Minimize2 className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); if (document.fullscreenElement) document.exitFullscreen(); onClose(); }}
              className="p-2 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
              title="Exit (Esc)"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Left/Right arrow buttons */}
        {currentIndex > 0 && (
          <button
            onClick={(e) => { e.stopPropagation(); goPrev(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-auto p-3 rounded-full bg-black/40 text-white/70 hover:text-white hover:bg-black/60 transition-colors"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
        )}
        {currentIndex < totalSlides - 1 && (
          <button
            onClick={(e) => { e.stopPropagation(); goNext(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-auto p-3 rounded-full bg-black/40 text-white/70 hover:text-white hover:bg-black/60 transition-colors"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        )}

        {/* Bottom progress bar */}
        <div className="absolute bottom-0 left-0 right-0 pointer-events-auto">
          {/* Slide thumbnail strip */}
          <div className="px-4 pb-3 flex justify-center">
            <div className="flex gap-1.5 overflow-x-auto max-w-[80vw] pb-1">
              {slides.map((slide, idx) => (
                <button
                  key={idx}
                  onClick={(e) => { e.stopPropagation(); setCurrentIndex(idx); }}
                  className={`flex-shrink-0 w-16 h-10 rounded overflow-hidden border-2 transition-all ${
                    idx === currentIndex
                      ? 'border-white shadow-lg scale-110'
                      : 'border-white/20 hover:border-white/50 opacity-60 hover:opacity-100'
                  }`}
                >
                  {slide.preview_url ? (
                    <img src={slide.preview_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-white/10" />
                  )}
                </button>
              ))}
            </div>
          </div>
          {/* Progress line */}
          <div className="h-1 bg-white/10">
            <div
              className="h-full bg-white/60 transition-all duration-300"
              style={{ width: `${((currentIndex + 1) / totalSlides) * 100}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
